import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { FlaskConical, Thermometer, Shield, Zap, CheckCircle, Cpu, Beaker, Sparkles } from 'lucide-react';

const TechnologyPage = () => {
  const { currentLanguage, t } = useLanguage();

  const content = {
    en: {
      badge: "Innovation",
      title: "Our Technology",
      subtitle: "MSSPT - Multi-Step Stabilization & Preservation Technology",
      intro: "At PenLab Peptide, we've developed a revolutionary approach to peptide stabilization that ensures maximum efficacy and convenience for researchers.",
      sections: [
        {
          title: "Multi-Step Stabilization Process",
          description: "Our proprietary MSSPT technology involves multiple stabilization steps that protect peptide integrity from synthesis to delivery.",
          features: [
            "Advanced purification techniques ensuring >99% purity",
            "pH optimization for maximum stability",
            "Proprietary excipient blend for long-term preservation",
            "Temperature-tolerant formulation design"
          ]
        },
        {
          title: "Pre-Mixed Pen Format",
          description: "Our innovative pen delivery system eliminates the need for reconstitution, providing a ready-to-use solution for researchers.",
          features: [
            "No mixing or reconstitution required",
            "Precise dosing with calibrated pen mechanism",
            "Reduced contamination risk",
            "Convenient storage and handling"
          ]
        },
        {
          title: "Temperature Tolerance",
          description: "Our stabilized formulas are designed to withstand short-term temperature deviations during shipping and handling.",
          features: [
            "Stable at room temperature for short periods",
            "Maintains integrity during transit",
            "Reduced cold chain requirements",
            "Extended shelf life compared to standard peptides"
          ]
        }
      ],
      benefits: {
        title: "Benefits for Researchers",
        items: [
          "Consistent results with standardized formulations",
          "Time savings with ready-to-use products",
          "Reduced waste from failed reconstitutions",
          "Higher reliability in research protocols"
        ]
      }
    },
    es: {
      badge: "Innovación",
      title: "Nuestra Tecnología",
      subtitle: "MSSPT - Tecnología de Estabilización y Preservación Multi-Paso",
      intro: "En PenLab Peptide, hemos desarrollado un enfoque revolucionario para la estabilización de péptidos que garantiza la máxima eficacia y conveniencia para los investigadores.",
      sections: [
        {
          title: "Proceso de Estabilización Multi-Paso",
          description: "Nuestra tecnología patentada MSSPT involucra múltiples pasos de estabilización que protegen la integridad del péptido desde la síntesis hasta la entrega.",
          features: [
            "Técnicas avanzadas de purificación garantizando >99% de pureza",
            "Optimización del pH para máxima estabilidad",
            "Mezcla de excipientes patentada para preservación a largo plazo",
            "Diseño de formulación tolerante a la temperatura"
          ]
        },
        {
          title: "Formato de Pluma Premezclada",
          description: "Nuestro innovador sistema de entrega en pluma elimina la necesidad de reconstitución, proporcionando una solución lista para usar para investigadores.",
          features: [
            "No se requiere mezcla ni reconstitución",
            "Dosificación precisa con mecanismo de pluma calibrado",
            "Riesgo reducido de contaminación",
            "Almacenamiento y manejo convenientes"
          ]
        },
        {
          title: "Tolerancia a la Temperatura",
          description: "Nuestras fórmulas estabilizadas están diseñadas para soportar desviaciones de temperatura a corto plazo durante el envío y manejo.",
          features: [
            "Estable a temperatura ambiente por períodos cortos",
            "Mantiene la integridad durante el tránsito",
            "Requisitos reducidos de cadena de frío",
            "Vida útil extendida comparada con péptidos estándar"
          ]
        }
      ],
      benefits: {
        title: "Beneficios para Investigadores",
        items: [
          "Resultados consistentes con formulaciones estandarizadas",
          "Ahorro de tiempo con productos listos para usar",
          "Reducción de desperdicios por reconstituciones fallidas",
          "Mayor confiabilidad en protocolos de investigación"
        ]
      }
    },
    ru: {
      badge: "Инновации",
      title: "Наша технология",
      subtitle: "MSSPT, многоступенчатая технология стабилизации и сохранения",
      intro: "В PenLab Peptide мы разработали революционный подход к стабилизации пептидов, обеспечивающий максимальную эффективность и удобство для исследователей.",
      sections: [
        {
          title: "Многоступенчатый процесс стабилизации",
          description: "Наша запатентованная технология MSSPT включает несколько этапов стабилизации, защищающих целостность пептида от момента синтеза до доставки.",
          features: [
            "Передовые методы очистки с гарантией чистоты более 99%",
            "Оптимизация pH для максимальной стабильности",
            "Запатентованная смесь вспомогательных веществ для длительного хранения",
            "Формула, устойчивая к температурным колебаниям"
          ]
        },
        {
          title: "Формат готовой шприц-ручки",
          description: "Наша инновационная система доставки в виде шприц-ручки исключает необходимость разведения, исследователи получают готовое к применению решение.",
          features: [
            "Не требует смешивания или разведения",
            "Точная дозировка благодаря калиброванному механизму",
            "Минимальный риск загрязнения",
            "Удобное хранение и использование"
          ]
        },
        {
          title: "Температурная устойчивость",
          description: "Наши стабилизированные формулы разработаны для выдерживания кратковременных температурных колебаний при доставке и обработке.",
          features: [
            "Стабильны при комнатной температуре в течение короткого периода",
            "Сохраняют целостность при транспортировке",
            "Сниженные требования к холодовой цепи",
            "Увеличенный срок годности по сравнению со стандартными пептидами"
          ]
        }
      ],
      benefits: {
        title: "Преимущества для исследователей",
        items: [
          "Стабильные результаты благодаря стандартизированным формулам",
          "Экономия времени с готовыми к применению продуктами",
          "Сокращение отходов от неудачных разведений",
          "Повышенная надёжность исследовательских протоколов"
        ]
      }
    },
    de: {
      badge: "Innovation",
      title: "Unsere Technologie",
      subtitle: "MSSPT - Mehrstufige Stabilisations- und Konservierungstechnologie",
      intro: "Bei PenLab Peptide haben wir einen revolutionären Ansatz zur Peptidstabilisierung entwickelt, der maximale Wirksamkeit und Komfort für Forschende gewährleistet.",
      sections: [
        {
          title: "Mehrstufiger Stabilisationsprozess",
          description: "Unsere proprietäre MSSPT-Technologie umfasst mehrere Stabilisierungsschritte, die die Peptidintegration vom Zeitpunkt der Synthese bis zur Auslieferung schützen.",
          features: [
            "Fortschrittliche Reinigungstechniken, die eine Reinheit von >99 % gewährleisten",
            "pH-Optimierung für maximale Stabilität",
            "Proprietäre Hilfsstoffmischung für Langzeitkonservierung",
            "Temperaturtolerantes Formulierungsdesign"
          ]
        },
        {
          title: "Vormischtes Pen-Format",
          description: "Unser innovatives Pen-Applikationssystem eliminiert die Notwendigkeit einer Rekonstitution und bietet eine gebrauchsfertige Lösung für Forschende.",
          features: [
            "Kein Mischen oder Rekonstituieren erforderlich",
            "Präzise Dosierung mit kalibriertem Pen-Mechanismus",
            "Reduziertes Kontaminationsrisiko",
            "Praktische Lagerung und Handhabung"
          ]
        },
        {
          title: "Temperaturtoleranz",
          description: "Unsere stabilisierten Formulierungen sind so konzipiert, dass sie kurzfristige Temperaturabweichungen während Versand und Handhabung überstehen.",
          features: [
            "Kurzfristig bei Raumtemperatur stabil",
            "Erhält die Integrität während des Transports",
            "Reduzierte Anforderungen an die Kühlkette",
            "Verlängerte Haltbarkeit im Vergleich zu Standardpeptiden"
          ]
        }
      ],
      benefits: {
        title: "Vorteile für Forschende",
        items: [
          "Konstante Ergebnisse mit standardisierten Formulierungen",
          "Zeitersparnis durch gebrauchsfertige Produkte",
          "Weniger Ausschuss durch fehlgeschlagene Rekonstitutionen",
          "Höhere Zuverlässigkeit in Forschungsprotokollen"
        ]
      }
    },
    fr: {
      badge: "Innovation",
      title: "Notre technologie",
      subtitle: "MSSPT - Technologie de stabilisation et de préservation multi-étapes",
      intro: "Chez PenLab Peptide, nous avons développé une approche révolutionnaire de la stabilisation des peptides qui garantit une efficacité maximale et une commodité optimale pour les chercheurs.",
      sections: [
        {
          title: "Processus de stabilisation multi-étapes",
          description: "Notre technologie propriétaire MSSPT implique plusieurs étapes de stabilisation qui protègent l’intégrité des peptides depuis la synthèse jusqu’à la livraison.",
          features: [
            "Techniques de purification avancées assurant une pureté >99 %",
            "Optimisation du pH pour une stabilité maximale",
            "Mélange d’excipients propriétaire pour une préservation à long terme",
            "Conception de formulation tolérante aux variations de température"
          ]
        },
        {
          title: "Format stylo pré-mélangé",
          description: "Notre système innovant d’administration par stylo élimine le besoin de reconstitution, offrant une solution prête à l’emploi pour les chercheurs.",
          features: [
            "Aucun mélange ni reconstitution requis",
            "Dosage précis grâce au mécanisme de stylo calibré",
            "Réduction du risque de contamination",
            "Stockage et manipulation pratiques"
          ]
        },
        {
          title: "Tolérance à la température",
          description: "Nos formules stabilisées sont conçues pour supporter des écarts de température de courte durée pendant le transport et la manipulation.",
          features: [
            "Stable à température ambiante pendant de courtes périodes",
            "Maintient son intégrité pendant le transport",
            "Besoins réduits en chaîne du froid",
            "Durée de conservation prolongée par rapport aux peptides standards"
          ]
        }
      ],
      benefits: {
        title: "Avantages pour les chercheurs",
        items: [
          "Résultats constants avec des formulations standardisées",
          "Gain de temps grâce aux produits prêts à l’emploi",
          "Réduction des pertes dues aux reconstitutions échouées",
          "Fiabilité accrue dans les protocoles de recherche"
        ]
      }
    },
    it: {
      badge: "Innovazione",
      title: "La Nostra Tecnologia",
      subtitle: "MSSPT - Tecnologia di Stabilizzazione e Conservazione Multi-Fase",
      intro: "In PenLab Peptide, abbiamo sviluppato un approccio rivoluzionario alla stabilizzazione dei peptidi che garantisce la massima efficacia e praticità per i ricercatori.",
      sections: [
        {
          title: "Processo di Stabilizzazione Multi-Fase",
          description: "La nostra tecnologia proprietaria MSSPT prevede più fasi di stabilizzazione che proteggono l'integrità dei peptidi dalla sintesi alla consegna.",
          features: [
            "Tecniche di purificazione avanzate che garantiscono una purezza >99%",
            "Ottimizzazione del pH per una stabilità massima",
            "Miscela di eccipienti proprietaria per la conservazione a lungo termine",
            "Progettazione della formulazione tollerante alla temperatura"
          ]
        },
        {
          title: "Formato Penna Pre-Miscelata",
          description: "Il nostro innovativo sistema di somministrazione a penna elimina la necessità di ricostituzione, offrendo una soluzione pronta all'uso per i ricercatori.",
          features: [
            "Nessuna miscelazione o ricostituzione necessaria",
            "Dosaggio preciso con meccanismo della penna calibrato",
            "Rischio di contaminazione ridotto",
            "Conservazione e gestione pratiche"
          ]
        },
        {
          title: "Tolleranza alla Temperatura",
          description: "Le nostre formule stabilizzate sono progettate per resistere a deviazioni di temperatura a breve termine durante la spedizione e la manipolazione.",
          features: [
            "Stabile a temperatura ambiente per brevi periodi",
            "Mantiene l'integrità durante il trasporto",
            "Requisiti ridotti per la catena del freddo",
            "Durata di conservazione prolungata rispetto ai peptidi standard"
          ]
        }
      ],
      benefits: {
        title: "Vantaggi per i Ricercatori",
        items: [
          "Risultati costanti con formulazioni standardizzate",
          "Risparmio di tempo con prodotti pronti all'uso",
          "Riduzione degli sprechi dovuti a ricostituzioni non riuscite",
          "Maggiore affidabilità nei protocolli di ricerca"
        ]
      }
    },
    pl: {
      badge: "Innowacja",
      title: "Nasza technologia",
      subtitle: "MSSPT - Technologia wieloetapowej stabilizacji i konserwacji",
      intro: "W PenLab Peptide opracowaliśmy rewolucyjne podejście do stabilizacji peptydów, które zapewnia maksymalną skuteczność i wygodę dla badaczy.",
      sections: [
        {
          title: "Wieloetapowy proces stabilizacji",
          description: "Nasza zastrzeżona technologia MSSPT obejmuje wiele etapów stabilizacji, które chronią integralność peptydu od syntezy aż do dostawy.",
          features: [
            "Zaawansowane techniki oczyszczania zapewniające czystość >99%",
            "Optymalizacja pH dla maksymalnej stabilności",
            "Zastrzeżona mieszanina substancji pomocniczych dla długoterminowej konserwacji",
            "Projekt formulacji tolerującej zmiany temperatury"
          ]
        },
        {
          title: "Wstępnie napełniony format pena",
          description: "Nasz innowacyjny system podawania za pomocą pena eliminuje konieczność rekonstytucji, zapewniając gotowe do użycia rozwiązanie dla badaczy.",
          features: [
            "Brak konieczności mieszania lub rekonstytucji",
            "Precyzyjne dawkowanie dzięki skalibrowanemu mechanizmowi pena",
            "Zmniejszone ryzyko zanieczyszczenia",
            "Wygodne przechowywanie i obsługa"
          ]
        },
        {
          title: "Tolerancja temperatury",
          description: "Nasze stabilizowane formuły są zaprojektowane tak, aby wytrzymywać krótkotrwałe odchylenia temperatury podczas wysyłki i obsługi.",
          features: [
            "Stabilność w temperaturze pokojowej przez krótkie okresy",
            "Zachowanie integralności podczas transportu",
            "Zredukowane wymagania dotyczące łańcucha chłodniczego",
            "Wydłużony okres trwałości w porównaniu ze standardowymi peptydami"
          ]
        }
      ],
      benefits: {
        title: "Korzyści dla badaczy",
        items: [
          "Spójne wyniki dzięki standaryzowanym formulacjom",
          "Oszczędność czasu dzięki produktom gotowym do użycia",
          "Mniejsze straty wynikające z nieudanych rekonstytucji",
          "Większa niezawodność w protokołach badawczych"
        ]
      }
    },
    nl: {
      badge: "Innovatie",
      title: "Onze Technologie",
      subtitle: "MSSPT - Multi-Step Stabilization & Preservation Technology",
      intro: "Bij PenLab Peptide hebben we een revolutionaire benadering van peptide-stabilisatie ontwikkeld die maximale werkzaamheid en gemak voor onderzoekers garandeert.",
      sections: [
        {
          title: "Multi-step stabilisatieproces",
          description: "Onze gepatenteerde MSSPT-technologie omvat meerdere stabilisatiestappen die de integriteit van peptiden beschermen van synthese tot levering.",
          features: [
            "Geavanceerde zuiveringstechnieken die >99% zuiverheid garanderen",
            "pH-optimalisatie voor maximale stabiliteit",
            "Gepatenteerde hulpstoffenmix voor langdurige conservering",
            "Temperatuurtolerant formuleringontwerp"
          ]
        },
        {
          title: "Vooraf gemengd penformaat",
          description: "Ons innovatieve penafgiftesysteem elimineert de noodzaak van reconstitutie en biedt een gebruiksklare oplossing voor onderzoekers.",
          features: [
            "Geen mengen of reconstitutie vereist",
            "Precieze dosering met gekalibreerd pennemechanisme",
            "Verminderd risico op contaminatie",
            "Handige opslag en hantering"
          ]
        },
        {
          title: "Temperatuurtolerantie",
          description: "Onze gestabiliseerde formules zijn ontworpen om kortdurende temperatuurschommelingen tijdens verzending en hantering te weerstaan.",
          features: [
            "Stabiel bij kamertemperatuur gedurende korte periodes",
            "Behoudt integriteit tijdens transport",
            "Verminderde eisen aan de koudeketen",
            "Langere houdbaarheid in vergelijking met standaard peptiden"
          ]
        }
      ],
      benefits: {
        title: "Voordelen voor onderzoekers",
        items: [
          "Consistente resultaten met gestandaardiseerde formuleringen",
          "Tijdsbesparing met gebruiksklare producten",
          "Minder verspilling door mislukte reconstituties",
          "Hogere betrouwbaarheid in onderzoeksprotocollen"
        ]
      }
    },
    pt: {
      badge: "Inovação",
      title: "Nossa Tecnologia",
      subtitle: "MSSPT - Tecnologia de Estabilização e Preservação em Múltiplas Etapas",
      intro: "Na PenLab Peptide, desenvolvemos uma abordagem revolucionária para a estabilização de peptídeos que garante máxima eficácia e conveniência para pesquisadores.",
      sections: [
        {
          title: "Processo de Estabilização em Múltiplas Etapas",
          description: "Nossa tecnologia proprietária MSSPT envolve múltiplas etapas de estabilização que protegem a integridade do peptídeo desde a síntese até a entrega.",
          features: [
            "Técnicas avançadas de purificação garantindo pureza >99%",
            "Otimização de pH para máxima estabilidade",
            "Mistura proprietária de excipientes para preservação de longo prazo",
            "Formulação tolerante à temperatura"
          ]
        },
        {
          title: "Formato de Caneta Pré-Misturada",
          description: "Nosso inovador sistema de aplicação em caneta elimina a necessidade de reconstituição, fornecendo uma solução pronta para uso para pesquisadores.",
          features: [
            "Nenhuma mistura ou reconstituição necessária",
            "Dosagem precisa com mecanismo de caneta calibrado",
            "Risco reduzido de contaminação",
            "Armazenamento e manuseio convenientes"
          ]
        },
        {
          title: "Tolerância à Temperatura",
          description: "Nossas fórmulas estabilizadas são projetadas para suportar desvios de temperatura de curto prazo durante o envio e o manuseio.",
          features: [
            "Estável em temperatura ambiente por curtos períodos",
            "Mantém a integridade durante o transporte",
            "Requisitos reduzidos de cadeia de frio",
            "Vida útil prolongada em comparação com peptídeos padrão"
          ]
        }
      ],
      benefits: {
        title: "Benefícios para Pesquisadores",
        items: [
          "Resultados consistentes com formulações padronizadas",
          "Economia de tempo com produtos prontos para uso",
          "Redução de desperdício devido a reconstituições malsucedidas",
          "Maior confiabilidade em protocolos de pesquisa"
        ]
      }
    },
  };

  const c = content[currentLanguage] || content.en;

  const sectionIcons = [
    <FlaskConical className="w-8 h-8 text-white" />,
    <Zap className="w-8 h-8 text-white" />,
    <Thermometer className="w-8 h-8 text-white" />
  ];

  const iconColors = [
    'from-capsule-teal to-capsule-aqua shadow-teal-glow',
    'from-green-500 to-emerald-500',
    'from-capsule-coral to-capsule-salmon shadow-coral-glow'
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* Hero Image Background */}
        <div className="absolute inset-0 opacity-30">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/bf28a057-bcbd-40cd-b04d-2b662e783ae3/images/53f8dec35799baeab137e063c9cf8e53b8056f3f8eb7de8cdcbb85ecc2acb32e.png"
            alt={t('technology_background_alt')}
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="techGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#techGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#techGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Cpu className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{c.badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {c.title}
            </h1>
            <p className="text-xl md:text-2xl text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua mb-6">
              {c.subtitle}
            </p>
            <p className="text-gray-300 text-lg max-w-2xl leading-relaxed">
              {c.intro}
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Technology Sections */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="space-y-20">
          {c.sections.map((section, index) => {
            // Different branded images for each section - PenLab Peptide
            const sectionImages = [
              "https://customer-assets.emergentagent.com/job_f8e2aef4-451e-4438-8a32-262b0341d7ed/artifacts/7mfh3mtr_WhatsApp%20Image%202026-02-07%20at%2001.04.11%20%282%29.jpeg",
              "https://customer-assets.emergentagent.com/job_f8e2aef4-451e-4438-8a32-262b0341d7ed/artifacts/iqx4lrvv_WhatsApp%20Image%202026-02-07%20at%2001.04.11%20%281%29.jpeg",
              "https://customer-assets.emergentagent.com/job_f8e2aef4-451e-4438-8a32-262b0341d7ed/artifacts/bxcx5kwr_WhatsApp%20Image%202026-02-07%20at%2001.04.11.jpeg"
            ];
            return (
            <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className={`w-16 h-16 bg-gradient-to-br ${iconColors[index]} rounded-2xl flex items-center justify-center mb-6`}>
                  {sectionIcons[index]}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{section.title}</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">{section.description}</p>
                <ul className="space-y-3">
                  {section.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center mt-0.5 flex-shrink-0">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={`glass-card rounded-3xl p-4 ${index % 2 === 1 ? 'md:order-1' : ''}`}>
                <img
                  src={sectionImages[index]}
                  alt={section.title}
                  className="rounded-2xl shadow-lg w-full h-64 md:h-80 object-cover"
                />
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">{c.benefits.title}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.benefits.items.map((item, index) => (
              <div key={index} className="bg-white/15 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <p className="text-white font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TechnologyPage;
